import React, { useEffect, useState } from "react";
import {
  useGetStakeholdersQuery,
  Stakeholder,
  Note,
  Attachment,
  usePatchStakeholdersByIdMutation,
  StakeholderPartial,
  StakeholderWithRelations,
  Action,
} from "../../../redux/vmsApi";
import {
  Box,
  CircularProgress,
  Typography,
  Grid,
  Card,
  CardContent,
  Divider,
  SelectChangeEvent,
  FormControl,
  MenuItem,
  Button,
  Select,
  Stack,
  TextField,
  InputLabel,
  Menu,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  FormControlLabel,
  Checkbox,
  Switch,
  Chip,
} from "@mui/material";
import VisibilityIcon from "@mui/icons-material/Visibility";
import { useAppSelector } from "../../../redux/hooks";
import DropzoneArea from "../../common/DropzoneArea";
import LoadingCover from "../../common/LoadingCover";
import { getCurrentDate } from "../../../utils/dateUtil";
import config from "../../../config";
import createStakeholderModel, {
  createStakeholderModelType,
} from "../../forms/models/createStakeholderModel";
import { Form, Formik } from "formik";
import CreateStakeholderForm from "../../forms/CreateStakeholderForm";

const StakeholdersList: React.FC = () => {
  const [isTaxClearance, setIsTaxClearance] = useState(false);
  const [isInternational, setIsInternational] = useState(false);
  const [bankingDetailsVerified, setBankingDetailsVerified] = useState(false);
  const [taxClearanceExpiryDate, setTaxClearanceExpiryDate] = useState("");

  const [filters, setFilters] = useState({
    name: "",
    type: "",
    supplierCode: "",
    customerCode: "",
    tin: "",
    paymentTerms: "",
    rating: 0,
    latestAction: "",
    limit: 10,
    skip: 0,
  });

  type MyFormValues = createStakeholderModelType;
  const [updateHistory, setUpdateHistory] = useState<Action[] | null>(null);

  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(10);
  const [apiParams, setApiParams] = useState({
    ...filters,
    skip: 0,
    limit: 10,
  });
  const [reportByEmailEnabled, setReportByEmailEnabled] = useState(false);

  const [filteredActions, setFilteredActions] = useState<string[] | null>(null);
  const user = useAppSelector((state) => state.auth.user);
  const [showOverlay, setShowOverlay] = useState(false);

  const {
    data: stakeholders,
    isLoading,
    isError,
  } = useGetStakeholdersQuery(apiParams);
  const [notes, setNotes] = useState<Note[] | null>(null);
  const [attachments, setAttachments] = useState<Attachment[]>([]);
  const [newComment, setNewComment] = useState("");

  const [isClicked, setIsClicked] = useState(false);
  const [isAddNotesOpen, setIsAddNotesOpen] = useState(false);
  const [patchStakeholdersById] = usePatchStakeholdersByIdMutation();
  const [selectedAction, setSelectedAction] = useState<any | null>("Notify");
  const handleCheckboxChange = (action: string) => {
    setSelectedAction((prev: string) => (prev === action ? null : action));
  };

  const [openDialog, setOpenDialog] = useState<string | null>(null);

  type DialogMode = "NOTES" | "UPDATE_SUPPLIER";

  const [dialogMode, setDialogMode] = useState<DialogMode>("NOTES");

  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [selectedStakeholder, setSelectedStakeholder] =
    useState<StakeholderWithRelations | null>(null);
  const handleOpen = (
    event: React.MouseEvent<HTMLButtonElement>,
    purchaseOrder: StakeholderWithRelations,
  ) => {
    setAnchorEl(event.currentTarget);
    setSelectedStakeholder(purchaseOrder);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const actions = ["Notify", "Request Verification", "Verify", "Suspend"];
  const userRoles = user?.roles || [];

  const handleAddNotes = (selectedStakeholder: StakeholderPartial) => {
    setIsInternational(Boolean(selectedStakeholder?.isInternational ?? false));
    setBankingDetailsVerified(Boolean(selectedStakeholder?.bankingDetailsVerified ?? false));
    if (selectedStakeholder?.notes) {
      setNotes(selectedStakeholder.notes);
    }

    if (selectedStakeholder?.updateHistory) {
      setUpdateHistory(selectedStakeholder.updateHistory);
    }

    const filtered = actions.filter((action) => {
      // Procurement-clerk can "Request Verification"
      if (action === "Request Verification") {
        if (
          !(
            userRoles.includes("procurement-clerk") ||
            userRoles.includes("admin")
          )
        ) {
          return false;
        }
      }

      // Finance Manager can "Verify"
      if (action === "Verify") {
        if (!(userRoles.includes("fm") || userRoles.includes("admin"))) {
          return false;
        }
      }

      // Only Finance Manager can "Suspend"
      if (action === "Suspend") {
        if (!(userRoles.includes("fm") || userRoles.includes("admin"))) {
          return false;
        }
      }

      //  Finance Manager can only "Verified" if clerk has requested Verification.
      const request =
        selectedStakeholder?.statusModificationRequest?.requestedAction;

      if (action === "Verified" && request !== "Request Verification") {
        return false;
      }

      //  Finance manager can only "Suspend" a verified Supplier.
      const fmVerification =
        selectedStakeholder?.statusModified?.requestedAction;

      if (action === "Suspend" && fmVerification !== "Verify") {
        return false;
      }

      return true;
    });

    setFilteredActions(filtered);
    setIsAddNotesOpen(true);
  };

  const handleAddNotesDialogClose = () => {
    setIsAddNotesOpen(false);
    setIsClicked(false);
    setNewComment("");
    setNotes(null);
    setIsInternational(false);
    setBankingDetailsVerified(false);
    setIsTaxClearance(false);
    setTaxClearanceExpiryDate("");
  };

  const handleOpenNotes = async (attachment: any) => {
    try {
      const fileName = attachment.name;
      if (!fileName) {
        console.error("No document attached");
      }
      setOpenDialog(`${config.CDN_URL}/procurement/${fileName}`);
    } catch (e) {
      console.error("Error opening document dialog", e);
      setOpenDialog(null);
    }
  };

  const handleDocumentDialogClose = () => {
    setOpenDialog(null);
  };

  if (isLoading) return <CircularProgress />;
  if (isError) return <div>Error fetching stakeholders.</div>;

  const handlePageSizeChange = (event: SelectChangeEvent<number>) => {
    const newSize = Number(event.target.value);
    setPageSize(newSize);
    setPage(0);
    setApiParams((prev) => ({
      ...prev,
      limit: newSize,
      skip: 0,
    }));
  };

  const handlePageChange = (newPage: number) => {
    setPage(newPage);
    setApiParams((prev) => ({
      ...prev,
      skip: newPage * pageSize,
    }));
  };

  const handleFilterChange = (field: string, value: string | boolean) => {
    setFilters((prevFilters) => ({
      ...prevFilters,
      [field]: value,
    }));
  };

  const handleApplyFilters = () => {
    setApiParams({
      ...filters,
      skip: 0,
      limit: pageSize,
    });
    setPage(0);
    setReportByEmailEnabled(false);
    handleFilterChange("reportName", "");
  };

  const handleSubmit = async () => {
    setIsClicked(true);
    setShowOverlay(true);
    if (selectedAction && newComment && user)
      try {
        const newNote: Note = {
          comment: newComment,
          action: {
            user,
            createdAt: getCurrentDate(),
            requestedAction: selectedAction,
          },
          attachments,
          isTaxClearance,
          taxClearanceExpiryDate,
        };

        if (newNote.action?.requestedAction === "Request Verification") {
          const currentDate = getCurrentDate();

          if (!isInternational) {
            if (!isTaxClearance) {
              alert(
                "Valid tax clearance is required before requesting Verification.",
              );
              handleAddNotesDialogClose();
              setShowOverlay(false);
              return;
            }

            if (
              !taxClearanceExpiryDate ||
              new Date(taxClearanceExpiryDate) <= new Date(currentDate)
            ) {
              alert(
                "Tax clearance expired. Please update tax clearance before requesting Verification.",
              );
              handleAddNotesDialogClose();
              setShowOverlay(false);
              return;
            }
          }
        }

        if (selectedStakeholder && selectedStakeholder.id) {
          if (!isInternational && isTaxClearance && taxClearanceExpiryDate) {
            const now = new Date(getCurrentDate());
            const expiry = new Date(taxClearanceExpiryDate);

            if (expiry < now) {
              alert("Tax clearance expired");
              handleAddNotesDialogClose();
              setShowOverlay(false);
              return;
            }
          }
          await patchStakeholdersById({
            id: selectedStakeholder.id,
            stakeholderPartial: {
              notes: [...(notes ?? []), newNote],
              latestAction: newNote.action,
              isInternational,
              bankingDetailsVerified,
              ...(isInternational ? {} : isTaxClearance && taxClearanceExpiryDate ? { taxClearanceExpiryDate } : {}),
            },
          }).unwrap();
        }

        setAttachments([]);
      } catch (error) {}
    handleAddNotesDialogClose();
    setShowOverlay(false);
  };
  const supplierInitialValues = {
    name: selectedStakeholder?.name ?? "",
    type: "Supplier",
    supplierCode: selectedStakeholder?.supplierCode ?? "",
    customerCode: selectedStakeholder?.customerCode ?? "",
    paymentTerms: selectedStakeholder?.paymentTerms ?? "",
    tin: selectedStakeholder?.tin ?? "",
    VATNumber: selectedStakeholder?.VATNumber ?? "",
    rating: selectedStakeholder?.rating ?? 0,
    isInternational: selectedStakeholder?.isInternational ?? false,
    bankingDetailsVerified: selectedStakeholder?.bankingDetailsVerified ?? false,
    contactInfo: selectedStakeholder?.contactInfo?.length
      ? selectedStakeholder.contactInfo
      : [{ email: "", phone: "", contactPerson: "" }],
    address: selectedStakeholder?.address?.length
      ? selectedStakeholder.address
      : [{ street: "", city: "", country: "" }],
  };

  const generateStakeholderUpdateDiff = (
    oldData: any,
    newData: any,
  ): string => {
    const changes: string[] = [];

    const compare = (oldVal: any, newVal: any, path = "") => {
      if (Array.isArray(oldVal) && Array.isArray(newVal)) {
        const maxLen = Math.max(oldVal.length, newVal.length);
        for (let i = 0; i < maxLen; i++) {
          compare(oldVal[i], newVal[i], `${path}[${i}]`);
        }
        return;
      }

      if (
        typeof oldVal === "object" &&
        oldVal !== null &&
        typeof newVal === "object" &&
        newVal !== null
      ) {
        Object.keys({ ...oldVal, ...newVal }).forEach((key) => {
          compare(oldVal?.[key], newVal?.[key], path ? `${path}.${key}` : key);
        });
        return;
      }

      if (oldVal !== newVal) {
        changes.push(`${path}:${oldVal ?? "empty"}→${newVal ?? "empty"}`);
      }
    };

    compare(oldData, newData);

    return `SUPPLIER_UPDATE_V1|${changes.join("|")}`;
  };

  const createStakeholderModelInitial = createStakeholderModel.initialValues;
  const initialValues: MyFormValues = {
    ...createStakeholderModelInitial,
  };
  const handleUpdateSupplier = async (values: MyFormValues) => {
    setIsClicked(true);
    setShowOverlay(true);

    if (!user || !selectedStakeholder?.id) return;

    try {
      const diffString = generateStakeholderUpdateDiff(selectedStakeholder, {
        ...selectedStakeholder,
        ...values,
      });

      const updateAction: Action = {
        user,
        createdAt: getCurrentDate(),
        requestedAction: diffString,
      };
      console.log(values);
      await patchStakeholdersById({
        id: selectedStakeholder.id,
        stakeholderPartial: {
          name: values.name,
          supplierCode: values.supplierCode,
          customerCode: values.customerCode,
          paymentTerms: values.paymentTerms,
          tin: values.tin,
          VATNumber: values.VATNumber,
          rating: values.rating,
          isInternational: Boolean(values.isInternational),
          bankingDetailsVerified: Boolean(values.bankingDetailsVerified),
          contactInfo: values.contactInfo,
          address: values.address,
          latestAction: updateAction,
          updateHistory: [...(updateHistory ?? []), updateAction],
        },
      }).unwrap();
    } catch (e) {
      console.error(e);
    } finally {
      setUpdateHistory([]);
      setShowOverlay(false);
      setIsClicked(false);
      handleAddNotesDialogClose();
    }
  };

  return (
    <React.Fragment>
      <Box p={6}>
        <Typography variant="h4" gutterBottom fontWeight="bold" color="#123456">
          View Stakeholders
        </Typography>

        {/* 🔍 Filters Section */}
        <Box
          sx={{
            padding: 3,
            borderRadius: 2,
            marginBottom: 3,
            backgroundColor: "#f9f9f9",
            boxShadow: 3,
          }}
        >
          <Typography
            variant="h6"
            gutterBottom
            sx={{ color: "#333", fontWeight: "bold" }}
          >
            Filters
          </Typography>

          <Box display="flex" flexWrap="wrap" gap={2} alignItems="center">
            <TextField
              label="Name"
              variant="outlined"
              size="small"
              fullWidth
              sx={{ maxWidth: 240, backgroundColor: "#fff", borderRadius: 1 }}
              value={filters.name}
              onChange={(e) => handleFilterChange("name", e.target.value)}
            />

            <FormControl
              size="small"
              fullWidth
              sx={{ maxWidth: 240, backgroundColor: "#fff", borderRadius: 1 }}
            >
              <InputLabel id="type-label">Type</InputLabel>
              <Select
                labelId="type-label"
                value={filters.type}
                label="Type"
                onChange={(e) => handleFilterChange("type", e.target.value)}
              >
                <MenuItem value="">All</MenuItem>
                <MenuItem value="Supplier">Supplier</MenuItem>
                <MenuItem value="Client">Client</MenuItem>
                <MenuItem value="Partner">Partner</MenuItem>
              </Select>
            </FormControl>

            <TextField
              label="Rating"
              variant="outlined"
              size="small"
              fullWidth
              sx={{ maxWidth: 240, backgroundColor: "#fff", borderRadius: 1 }}
              value={filters.rating}
              onChange={(e) => handleFilterChange("rating", e.target.value)}
            />
          </Box>

          <FormControl
            size="small"
            fullWidth
            sx={{
              maxWidth: 240,
              backgroundColor: "#fff",
              borderRadius: 1,
              mt: 1,
            }}
          >
            <InputLabel id="latestAction-select-label">
              Action Required{" "}
            </InputLabel>
            <Select
              labelId="latestAction-select-label"
              value={filters.latestAction || ""}
              label="Action Required"
              onChange={(e) =>
                handleFilterChange("latestAction", e.target.value)
              }
            >
              <MenuItem value="">All</MenuItem>
              <MenuItem value="Request Verification">
                Request Verifications
              </MenuItem>
              <MenuItem value="Notify">Notifications</MenuItem>
              <MenuItem value="Verify">Verified</MenuItem>
              <MenuItem value="Suspend">Suspended</MenuItem>
            </Select>
          </FormControl>
          <Stack direction="row" spacing={2} mt={2}>
            <Button
              variant="outlined"
              size="large"
              fullWidth
              onClick={handleApplyFilters}
              sx={{
                textTransform: "none",
                fontWeight: "bold",
                color: "#1976d2",
                borderColor: "#1976d2",
                "&:hover": {
                  backgroundColor: "#e3f2fd",
                  borderColor: "#1565c0",
                },
              }}
            >
              Apply Filters
            </Button>
          </Stack>
        </Box>

        {isLoading ? (
          <Typography>Loading...</Typography>
        ) : (
          <>
            <Grid container spacing={3}>
              {stakeholders?.map((stakeholder: Stakeholder) => (
                <Grid item xs={12} key={stakeholder.id}>
                  <Card
                    variant="outlined"
                    sx={{ borderRadius: 3, boxShadow: 3 }}
                  >
                    <CardContent>
                      <Box
                        display="flex"
                        justifyContent="space-between"
                        alignItems="center"
                        mb={1}
                      >
                        <Typography variant="h6" fontWeight="bold">
                          {stakeholder.name}
                        </Typography>
                      </Box>

                      <Typography
                        variant="subtitle2"
                        color="text.secondary"
                        gutterBottom
                      >
                        Type: {stakeholder.type}
                      </Typography>

                      {/* --- STATUS BADGES SECTION --- */}
                      <Box display="flex" flexWrap="wrap" gap={1} mt={1} mb={2}>
                        {stakeholder.isInternational && (
                          <Chip
                            label="International"
                            color="info"
                            variant="outlined"
                            size="small"
                            sx={{ fontWeight: "bold" }}
                          />
                        )}

                        {stakeholder.bankingDetailsVerified && (
                          <Chip
                            label="Banking Details Verified"
                            color="success"
                            variant="outlined"
                            size="small"
                            sx={{ fontWeight: "bold" }}
                          />
                        )}

                        {!stakeholder.isInternational && stakeholder.taxClearanceExpiryDate && (
                          <Chip
                            label={`Tax Clearance Expires: ${new Date(
                              stakeholder.taxClearanceExpiryDate,
                            ).toLocaleDateString()}`}
                            color={
                              new Date(stakeholder.taxClearanceExpiryDate) <
                              new Date()
                                ? "error"
                                : "primary"
                            }
                            variant="outlined"
                            size="small"
                            sx={{ fontWeight: "bold" }}
                          />
                        )}

                        {!stakeholder.isInternational && (
                          stakeholder.isTaxClearanceValid ? (
                            <Chip
                              label="Tax Clearance Valid"
                              color="success"
                              variant="outlined"
                              size="small"
                              sx={{ fontWeight: "bold" }}
                            />
                          ) : (
                            <Chip
                              label="No Valid Tax Clearance Available"
                              color="error"
                              variant="filled"
                              size="small"
                              sx={{ fontWeight: "bold" }}
                            />
                          )
                        )}

                        {stakeholder.isStakeholderVerified ? (
                          <Chip
                            label="Verified"
                            color="success"
                            variant="filled"
                            size="small"
                            sx={{ fontWeight: "bold" }}
                          />
                        ) : (
                          <Chip
                            label="Not Verified"
                            color="warning"
                            variant="outlined"
                            size="small"
                            sx={{ fontWeight: "bold" }}
                          />
                        )}

                        {stakeholder.suspended && (
                          <Chip
                            label="Suspended"
                            color="error"
                            variant="filled"
                            size="small"
                            sx={{ fontWeight: "bold" }}
                          />
                        )}
                      </Box>

                      <Divider sx={{ my: 2 }} />

                      <Grid container spacing={2}>
                        <Grid item xs={12} sm={6}>
                          <Typography
                            variant="subtitle2"
                            fontWeight="bold"
                            gutterBottom
                          >
                            Contact Info
                          </Typography>
                          {stakeholder.contactInfo?.map((contact, index) => (
                            <Box
                              key={index}
                              sx={{
                                mb: 2,
                                p: 1,
                                border: "1px solid #e0e0e0",
                                borderRadius: 2,
                                backgroundColor: "#fafafa",
                              }}
                            >
                              <Typography fontWeight="bold">
                                {contact.contactPerson}
                              </Typography>
                              <Typography
                                variant="body2"
                                color="text.secondary"
                              >
                                {contact.phone}
                              </Typography>
                              <Typography
                                variant="body2"
                                color="text.secondary"
                              >
                                {contact.email}
                              </Typography>
                            </Box>
                          ))}
                        </Grid>

                        <Grid item xs={12} sm={6}>
                          <Typography
                            variant="subtitle2"
                            fontWeight="bold"
                            gutterBottom
                          >
                            Address
                          </Typography>
                          {stakeholder.address?.map((addr, index) => (
                            <Box
                              key={index}
                              sx={{
                                mb: 2,
                                p: 1,
                                border: "1px solid #e0e0e0",
                                borderRadius: 2,
                                backgroundColor: "#fafafa",
                              }}
                            >
                              <Typography variant="body2">
                                {addr.street}, {addr.city}, {addr.country}
                              </Typography>
                            </Box>
                          ))}
                        </Grid>
                      </Grid>

                      <Divider sx={{ my: 2 }} />

                      {/* NEW SECTION: VAT, TIN, Supplier Code*/}
                      <Box mb={2}>
                        <Typography
                          variant="subtitle2"
                          fontWeight="bold"
                          gutterBottom
                        >
                          Additional Info
                        </Typography>

                        {stakeholder.VATNumber && (
                          <Typography variant="body2">
                            <strong>VAT Number:</strong> {stakeholder.VATNumber}
                          </Typography>
                        )}

                        {stakeholder.tin && (
                          <Typography variant="body2">
                            <strong>TIN:</strong> {stakeholder.tin}
                          </Typography>
                        )}

                        {stakeholder.supplierCode && (
                          <Typography variant="body2">
                            <strong>Supplier Code:</strong>{" "}
                            {stakeholder.supplierCode}
                          </Typography>
                        )}

                        {/* Show 'N/A' if nothing exists */}
                        {!stakeholder.VATNumber &&
                          !stakeholder.tin &&
                          !stakeholder.supplierCode && (
                            <Typography variant="body2" color="text.secondary">
                              No additional info available.
                            </Typography>
                          )}
                      </Box>

                      <Divider sx={{ my: 2 }} />

                      {/* --- RATING & PAYMENT TERMS --- */}
                      <Box display="flex" justifyContent="space-between">
                        <Typography variant="body2">
                          <strong>Rating:</strong> {stakeholder.rating || "N/A"}
                        </Typography>
                        <Typography variant="body2">
                          <strong>Payment Terms:</strong>{" "}
                          {stakeholder.paymentTerms || "N/A"}
                        </Typography>
                      </Box>

                      <Button
                        variant="contained"
                        size="small"
                        onClick={(e) => handleOpen(e, stakeholder)}
                        sx={{ mt: 2 }}
                      >
                        Actions
                      </Button>

                      <Menu
                        anchorEl={anchorEl}
                        open={Boolean(anchorEl)}
                        onClose={handleClose}
                        slotProps={{
                          paper: {
                            elevation: 3,
                            sx: { minWidth: 200, borderRadius: 1 },
                          },
                        }}
                      >
                        {selectedStakeholder && [
                          <MenuItem
                            key="add-view-notes"
                            onClick={() => {
                              handleClose();
                              handleAddNotes(selectedStakeholder);
                              setDialogMode("NOTES");
                            }}
                          >
                            Add/View notes
                          </MenuItem>,
                          <MenuItem
                            key="update-supplier"
                            onClick={() => {
                              handleClose();
                              handleAddNotes(selectedStakeholder);
                              setDialogMode("UPDATE_SUPPLIER");
                              console.log(selectedStakeholder);
                            }}
                          >
                            Update Supplier
                          </MenuItem>,
                        ]}
                      </Menu>
                    </CardContent>
                  </Card>
                </Grid>
              ))}
            </Grid>

            {/* Pagination Controls */}
            <Box
              display="flex"
              justifyContent="center"
              alignItems="center"
              mt={4}
            >
              <Button
                onClick={() => handlePageChange(Math.max(page - 1, 0))}
                disabled={page === 0}
                variant="contained"
                sx={{ marginRight: 2 }}
              >
                Previous
              </Button>

              <Typography variant="body1">Page {page + 1}</Typography>

              <Button
                onClick={() => handlePageChange(page + 1)}
                disabled={stakeholders && stakeholders?.length < pageSize}
                variant="contained"
                sx={{ marginLeft: 2 }}
              >
                Next
              </Button>
            </Box>

            <Box
              mt={2}
              display="flex"
              justifyContent="center"
              alignItems="center"
            >
              <Typography variant="body2" sx={{ marginRight: 2 }}>
                Rows per page:
              </Typography>
              <FormControl variant="outlined" size="small">
                <Select
                  value={pageSize}
                  onChange={handlePageSizeChange}
                  sx={{ backgroundColor: "#f0f4f7" }}
                >
                  <MenuItem value={5}>5</MenuItem>
                  <MenuItem value={10}>10</MenuItem>
                  <MenuItem value={20}>20</MenuItem>
                </Select>
              </FormControl>
            </Box>
          </>
        )}
      </Box>

      <Dialog
        open={isAddNotesOpen}
        onClose={handleAddNotesDialogClose}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          {dialogMode === "NOTES" ? "Add Notes" : "Update Supplier"}
        </DialogTitle>

        <DialogContent>
          {showOverlay && <LoadingCover />}
          {dialogMode === "NOTES" && (
            <>
              <Card
                elevation={3}
                sx={{
                  mb: 3,
                  p: 2,
                  borderRadius: 2,
                  background:
                    "linear-gradient(135deg, #f5f7fa 0%, #e4edf5 100%)",
                  borderLeft: "4px solid #1976d2",
                  boxShadow: "0 4px 20px 0 rgba(0,0,0,0.12)",
                  transition: "transform 0.3s, box-shadow 0.3s",
                  "&:hover": {
                    transform: "translateY(-2px)",
                    boxShadow: "0 6px 24px 0 rgba(0,0,0,0.15)",
                  },
                }}
              >
                <Typography variant="h6" gutterBottom>
                  Existing Notes
                </Typography>
                {notes && notes.length === 0 && (
                  <Typography variant="body2" color="textSecondary">
                    No notes yet
                  </Typography>
                )}

                {notes &&
                  notes.map((note, idx) => (
                    <Card key={idx} sx={{ mb: 2, p: 2 }}>
                      <Typography variant="body1">{note.comment}</Typography>
                      <Typography variant="caption" color="textSecondary">
                        {note.action.user?.name} •{" "}
                        {new Date(note.action.createdAt ?? "").toLocaleString(
                          "en-GB",
                          {
                            day: "2-digit",
                            month: "2-digit",
                            year: "numeric",
                          },
                        )}
                      </Typography>

                      {notes &&
                        note.attachments &&
                        note?.attachments?.length > 0 && (
                          <ul>
                            {note.attachments.map((att, i) => (
                              <li key={i}>
                                <a target="_blank" rel="noopener noreferrer">
                                  {att.name}
                                </a>
                                <IconButton
                                  onClick={() => handleOpenNotes(att)}
                                  sx={{ color: "#1976d2" }}
                                >
                                  <VisibilityIcon />
                                </IconButton>
                              </li>
                            ))}
                          </ul>
                        )}
                    </Card>
                  ))}
              </Card>

              <Grid item xs={12}>
                <TextField
                  label="Comment"
                  multiline
                  fullWidth
                  rows={3}
                  value={newComment}
                  onChange={(e) => setNewComment(e.target.value)}
                  sx={{ mb: 2 }}
                />
                <Typography
                  variant="subtitle1"
                  gutterBottom
                  sx={{ color: "textSecondary" }}
                >
                  Attachments
                </Typography>
                <DropzoneArea
                  setAttachments={setAttachments}
                  attachments={attachments}
                  fieldName="documents"
                />

                {selectedAction === "Request Verification" && (
                <Box sx={{ mt: 3, mb: 2 }}>
                  <Typography
                    variant="subtitle1"
                    gutterBottom
                    sx={{ color: "textSecondary" }}
                  >
                    Tax Clearance
                  </Typography>

                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={isInternational}
                        onChange={(e) => setIsInternational(e.target.checked)}
                        color="primary"
                      />
                    }
                    label="International"
                    sx={{ ml: 1 }}
                  />

                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={bankingDetailsVerified}
                        onChange={(e) => setBankingDetailsVerified(e.target.checked)}
                        color="primary"
                      />
                    }
                    label="Banking Details Verified"
                    sx={{ ml: 1 }}
                  />

                  {!isInternational && (
                    <>
                      <FormControlLabel
                        control={
                          <Switch
                            checked={isTaxClearance}
                            onChange={(e) => setIsTaxClearance(e.target.checked)}
                            color="primary"
                          />
                        }
                        label="This includes a Tax Clearance"
                        sx={{ ml: 1 }}
                      />

                      {isTaxClearance && (
                        <TextField
                          label="Tax Clearance Expiry Date"
                          type="date"
                          InputLabelProps={{ shrink: true }}
                          size="small"
                          fullWidth
                          sx={{
                            maxWidth: 300,
                            mt: 2,
                            bgcolor: "#fff",
                            borderRadius: 1,
                          }}
                          value={taxClearanceExpiryDate}
                          onChange={(e) =>
                            setTaxClearanceExpiryDate(e.target.value)
                          }
                        />
                      )}
                    </>
                  )}
                </Box>
                )}

                <Box sx={{ mt: 2 }}>
                  <Typography
                    variant="subtitle1"
                    gutterBottom
                    sx={{ color: "textSecondary" }}
                  >
                    Actions
                  </Typography>

                  <Box sx={{ display: "flex", flexWrap: "wrap", gap: 2 }}>
                    {filteredActions &&
                      filteredActions.map((action) => (
                        <FormControlLabel
                          key={action}
                          control={
                            <Checkbox
                              checked={selectedAction === action}
                              onChange={() => handleCheckboxChange(action)}
                              disabled={
                                selectedAction !== null &&
                                selectedAction !== action
                              }
                            />
                          }
                          label={action}
                        />
                      ))}
                  </Box>
                </Box>
              </Grid>
            </>
          )}

          {dialogMode === "UPDATE_SUPPLIER" && (
            <Formik
              enableReinitialize
              initialValues={supplierInitialValues}
              onSubmit={handleUpdateSupplier}
            >
              <Form id="formik-form">
                <CreateStakeholderForm />
              </Form>
            </Formik>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleAddNotesDialogClose}>Cancel</Button>
          {dialogMode === "NOTES" && (
            <Button
              variant="contained"
              onClick={handleSubmit}
              disabled={isClicked}
            >
              Submit
            </Button>
          )}

          {dialogMode === "UPDATE_SUPPLIER" && (
            <Button
              variant="contained"
              color="secondary"
              type="submit"
              form="formik-form"
              disabled={isClicked}
            >
              Update Supplier
            </Button>
          )}
        </DialogActions>
      </Dialog>

      <Dialog open={!!openDialog} onClose={handleDocumentDialogClose} fullWidth maxWidth="md">
        <Box p={2}>
          {openDialog && (
            <iframe
              src={openDialog}
              title="Document Viewer"
              width="100%"
              height="500px"
              style={{ border: "none" }}
            />
          )}
          <Box textAlign="right" mt={2}>
            <Button onClick={handleDocumentDialogClose} variant="contained" color="primary">
              Close
            </Button>
          </Box>
        </Box>
      </Dialog>
    </React.Fragment>
  );
};

export default StakeholdersList;
